'use client';
import Button from '@/components/Button';
import Field from '@/components/Field';
import FormError from '@/components/FormError';
import { fullPageLoadingIndicator } from '@/components/LoadingIndicator';
import { Banner } from '@/components/MedicalProfessionalDiscounts/banner';
import { SuccessDialog } from '@/components/MedicalProfessionalDiscounts/successDialog';
import TextInput from '@/components/TextInput';
import Upload from '@/components/Upload';
import useMilitaryDiscountsPage from '@/hooks/MilitaryDiscountsPage/useMilitaryDiscountsPage';
import combine from '@/util/combineValidators';
import { isRequired, validateEmail } from '@/util/formValidators';
import { Form } from 'informed';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import classes from './militaryDiscountsPage.module.css';

const MilitaryDiscountsPage = props => {
  const { formatMessage } = useIntl();

  const uploadRef = useRef(null);

  const talonProps = useMilitaryDiscountsPage({
    uploadRef,
  });

  const { isLoading, errors, handleSubmit, response, formApiRef } = talonProps;

  const maybeLoadingIndicator = isLoading ? fullPageLoadingIndicator : null;
  const [showModal, setShowModal] = useState(false);
  useEffect(() => {
    if (response && response.code === '200') {
      setShowModal(true);
    }
  }, [response]);
  const removeIcon = (
    <Image
      className='removeIcon h-[13px] w-[13px] lg_h-[20px] lg_w-[20px]'
      src='upload-remove.svg'
      alt='remove icon'
      width={20}
      height={20}
    />
  );

  return (
    <div className='pb-[40px] lg_pb-[80px]'>
      <Banner
        url={'/discount/Military-banner'}
        title='15% OFF Military Discount'
        text='Thank you for your dedication and service. <br />We’re proud to support you!'
      />
      {/* {maybeLoadingIndicator} */}
      <article className='personalContent mx-auto'>
        <div className='mb-[40px] px-[12px] lg_mb-[60px] lg_px-0'>
          <div className=''>
            <FormError
              allowErrorMessages
              errors={Array.from(errors.values())}
            />
            <Form
              className={classes.form}
              onSubmit={handleSubmit}
              formApiRef={formApiRef}
            >
              <div className=' flex flex-col justify-between lg_flex-row'>
                <Field
                  id='first-name'
                  classes={{
                    root: classes.nameField,
                    requiredLabel: classes.requiredLabel,
                    labelText: classes.labelText,
                  }}
                  label='First Name'
                  required
                >
                  <TextInput
                    autoComplete='firstName'
                    field='firstname'
                    id='firstname'
                    validate={isRequired}
                    // placeholder={formatMessage({
                    //   id: 'militaryDiscountsPage.firstNamePlaceholder',
                    //   defaultMessage: `First Name*`,
                    // })}
                  />
                </Field>
                <Field
                  id='last-name'
                  classes={{
                    root: classes.nameField,
                    requiredLabel: classes.requiredLabel,
                    labelText: classes.labelText,
                  }}
                  label='Last Name'
                  required
                >
                  <TextInput
                    autoComplete='lastName'
                    field='lastname'
                    id='lastname'
                    validate={isRequired}
                    // placeholder={formatMessage({
                    //   id: 'militaryDiscountsPage.lastNamePlaceholder',
                    //   defaultMessage: `Last Name*`,
                    // })}
                  />
                </Field>
              </div>
              <div className='mb-[20px]'>
                <Field
                  id='contact-email'
                  classes={{
                    root: classes.emailField,
                    requiredLabel: classes.requiredLabel,
                    labelText: classes.labelText,
                  }}
                  label='Email'
                  required
                >
                  <TextInput
                    autoComplete='email'
                    field='email'
                    id='contact-email'
                    validate={combine([isRequired, validateEmail])}
                    // placeholder={formatMessage({
                    //   id: 'militaryDiscountsPage.emailPlaceholder',
                    //   defaultMessage: 'Email*',
                    // })}
                  />
                </Field>
              </div>

              <div className='mb-[10px]'>
                <Upload
                  ref={uploadRef}
                  showUploadList={{
                    showDownloadIcon: false,
                    showPreviewIcon: false,
                    showRemoveIcon: true,
                    removeIcon: removeIcon,
                  }}
                />
              </div>
              <p className='mb-[5px] flex items-center justify-start gap-[10px] lg_mb-[10px]'>
                <Image
                  src={`/discount/pic-icon.svg`}
                  alt='Upload Icon'
                  width={22}
                  height={20}
                  className='h-[20px] w-[22px]'
                />
                <span className='font-roboto-medium text-[16px] font-medium leading-[24px]'>
                  Photo of Your Military ID
                </span>
              </p>
              <p className='mb-[10px] font-roboto-light text-[12px] leading-[20px]'>(1 image, JPG/PNG only)</p>
              <p className='mb-[20px] font-roboto-regular text-[16px] font-normal leading-[24px]'>
                Note: We only need your name to be visible. Feel free to cover up any other sensitive details.
              </p>

              <div className=''>
                <Button
                  priority='high'
                  type='submit'
                  disabled={isLoading}
                  loading={isLoading}
                  className='duration-300 h-[40px] w-full rounded-full bg-black font-roboto-medium text-[16px] leading-[40px] text-white transition-colors hover_bg-[#4C4C4C] lg_h-[50px] lg_w-[140px] lg_text-[18px] lg_leading-[50px]'
                >
                  <FormattedMessage
                    id={'militaryDiscountsPage.submit'}
                    defaultMessage={'Submit'}
                  />
                </Button>
              </div>
            </Form>
          </div>
        </div>
        <div className='lg-px-0 bg-[#F9F9F9] px-[12px] pb-[30px] pt-[40px] lg_bg-white  lg_pt-0'>
          <h2 className='mb-[20px] font-teko-medium text-[30px] uppercase leading-[30px] lg_mb-[10px] lg_text-[40px] lg_leading-[40px]'>
            Frequently Asked Questions
          </h2>
          <p className='mb-[10px] font-roboto-medium text-[16px] font-medium leading-[19px] lg_text-[18px] lg_leading-[21px]'>
            1. Who is eligible for the Military Discount?
          </p>
          <ul className='mb-[20px]'>
            {['Active-duty service members', 'Veterans and retirees', 'Reservists', 'Military academy students'].map(
              (item, index) => (
                <li
                  key={index}
                  className='mb-[10px] flex items-baseline gap-[10px]'
                >
                  <i className='relative top-[-2px] inline-block !h-[6px] min-h-[6px] !w-[6px] min-w-[6px] rounded-[3px] bg-black'></i>
                  <span
                    className='font-roboto-light text-[14px] font-light leading-[20px] lg_text-[16px] lg_leading-[26px]'
                    dangerouslySetInnerHTML={{ __html: item }}
                  />
                </li>
              ),
            )}
          </ul>
          <p className='mb-[10px] font-roboto-medium text-[16px] font-medium leading-[19px] lg_text-[18px] lg_leading-[21px]'>
            2. How do I verify my eligibility?
          </p>
          <p className='mb-[20px] font-roboto-light text-[14px] font-light leading-[20px] lg_text-[16px] lg_leading-[26px]'>
            Verification is quick and simple! Just fill out the form above and submit a photo of your military ID so we
            can confirm your eligibility for the military discount. We’ll verify your information within 48 hours and
            email you the discount code. <br />
            <br />
            Please make sure to enter your first and last name exactly as they appear on your military ID.
          </p>
          <p className='mb-[10px] font-roboto-medium text-[16px] font-medium leading-[19px] lg_text-[18px] lg_leading-[21px]'>
            3. Does the Military Discount apply to past orders?
          </p>
          <p className='mb-[40px] font-roboto-light text-[14px] font-light leading-[20px] lg_mb-[60px] lg_text-[16px] lg_leading-[26px]'>
            Unfortunately, the discount cannot be applied to past orders placed on HISEA.com or through other sales
            channels, such as Amazon, eBay, or Walmart. The discount can only be used for current and future orders.
          </p>
          <h2 className='mb-[20px] font-teko-medium text-[30px] uppercase leading-[30px] lg_mb-[10px] lg_text-[40px] lg_leading-[40px]'>
            Military Discount Terms and Conditions
          </h2>
          <ul className=''>
            {[
              'The discount takes 15% off your order total, excluding shipping fees.',
              'Only one discount code can be used per order, and the discount is valid only on full-price items.',
              'The discount code must be used on an order placed under the same name and email address you submitted. Orders placed under a different name or email address may be canceled without notice.',
              'Orders placed before receiving the discount code are not eligible for reimbursement.',
              'By submitting this form, you confirm that you have read and agree to these Terms and Conditions.',
              'HISEA.com will not collect or store the information you provide. It will only be used to verify your eligibility for the military discount.',
              'If you have trouble receiving your discount code or have any questions, please contact <a href="mailto:cs@hisea.com" style="text-decoration: underline; font-weight: 600;">cs@hisea.com</a>.',
            ].map((item, index) => (
              <li
                key={index}
                className='mb-[10px] flex items-baseline gap-[10px]'
              >
                <i className='relative top-[-2px] inline-block !h-[6px] min-h-[6px] !w-[6px] min-w-[6px] rounded-[3px] bg-black'></i>
                <span
                  className='font-roboto-light text-[14px] font-light leading-[20px] lg_text-[16px] lg_leading-[26px]'
                  dangerouslySetInnerHTML={{ __html: item }}
                />
              </li>
            ))}
          </ul>
        </div>
      </article>
      {showModal && (
        <SuccessDialog
          titlePc='Application Received-Thank You for Your Service!'
          titleMobile='Application Received<br/>Thank You for Your Service!'
          textPc={[
            'We appreciate your application for our military discount. ',
            'Our team is verifying information, which typically takes 1-2 business days.',
            "Once approved, you'll receive an email with your unique discount code.",
          ]}
          textMobile={[
            'We appreciate your application for our military discount. ',
            'Our team is verifying information, which typically takes 1-2 business days.',
            "Once approved, you'll receive an email with your unique discount code.",
          ]}
          handleClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};

export default MilitaryDiscountsPage;
